/**
 * QuickMonitorTab.jsx
 *
 * Simplified per-ticker overview designed for fast decision-making.
 * Everything important about a symbol on one card — no signal pips,
 * no detailed indicator grids.
 *
 * Per ticker shows:
 *   - Regime badge (RANGING / WEAK TREND / TRENDING / STRONG TREND)
 *   - Direction (↑ Bullish / ↓ Bearish / → Neutral) + RSI + VWAP dev + score
 *   - Trend summary: one plain-English sentence (continuing / reversing / etc.)
 *   - Latest 1 alert for that ticker
 */

import { TIER_META } from '../../lib/alertTiers';
import AdvicePanel   from './AdvicePanel';

// ── Regime classification (matches VolatilityPanel thresholds) ────────────────
const REGIME_LEVELS = [
  { max: 15,  label: 'Strong Range', short: 'RANGE',        color: '#22d3ee', scalp: 'Bet against extremes — when price spikes up or crashes down, it will likely snap back. Short the spike, buy the crash.' },
  { max: 20,  label: 'Ranging',      short: 'RANGE',        color: '#22d3ee', scalp: 'Bet against extremes — price is bouncing in a range. Buy low, sell high. Wait for RSI oversold/overbought before entering.' },
  { max: 25,  label: 'Weak Trend',   short: 'MIXED',        color: '#f59e0b', scalp: 'Unclear — a trend may be forming. Reduce size, tighten your stop losses. Don\'t bet against a move that might keep going.' },
  { max: 35,  label: 'Trending',     short: 'TREND',        color: '#f97316', scalp: 'Follow the move, don\'t fight it — if it\'s going up, look to buy dips. If down, look to sell bounces. Shorting a spike will hurt you here.' },
  { max: 999, label: 'Strong Trend', short: 'STRONG TREND', color: '#ef4444', scalp: 'Don\'t bet against this move — the momentum is too strong to fade. Either trade with it or sit out. Trying to pick a reversal here is high risk.' },
];

function getRegime(adx) {
  if (adx == null) return { label: '—', short: '—', color: 'var(--faint)' };
  return REGIME_LEVELS.find(t => adx < t.max) ?? REGIME_LEVELS[REGIME_LEVELS.length - 1];
}

// ── Trend summary sentence ────────────────────────────────────────────────────
// Derives a plain-English one-liner from available intraday signals.
function deriveTrendSummary(s) {
  if (!s) return { text: 'Waiting for data…', color: 'var(--faint)' };

  const { rsi, rsiSlope, pDI, mDI, adx, vwapDev, confluence } = s;
  const dir = confluence?.direction ?? 'neutral';

  // Coil overrides everything
  if (s.coil?.coiling) {
    return { text: `Coiled ${s.coil.daysInBand} days — no directional edge. Wait for the break.`, color: 'var(--down)' };
  }

  // Strong trend continuation signals
  if (adx != null && adx >= 30) {
    if (pDI != null && mDI != null) {
      const trendDir = pDI > mDI ? 'bullish' : 'bearish';
      const slopeConfirms = rsiSlope != null && ((trendDir === 'bullish' && rsiSlope > 2) || (trendDir === 'bearish' && rsiSlope < -2));
      const word = trendDir === 'bullish' ? 'uptrend' : 'downtrend';
      if (slopeConfirms) return { text: `Strong ${word} — momentum confirming. Avoid fading.`, color: trendDir === 'bullish' ? 'var(--up)' : 'var(--down)' };
      return { text: `Strong ${word} — ADX ${adx?.toFixed(0)}, trend intact. Don't fade it.`, color: trendDir === 'bullish' ? 'var(--up)' : 'var(--down)' };
    }
  }

  // RSI extreme + RSI slope: reversal signal detection
  if (rsi != null && rsiSlope != null) {
    // RSI deep oversold, but slope turning up = early reversal forming
    if (rsi < 30 && rsiSlope > 3) return { text: `Oversold (RSI ${rsi.toFixed(0)}) + RSI turning up — potential bullish reversal forming.`, color: 'var(--up)' };
    // RSI deep oversold, slope still falling = continuation down
    if (rsi < 30 && rsiSlope < -1) return { text: `Oversold (RSI ${rsi.toFixed(0)}) and still falling — bearish momentum continuing.`, color: 'var(--down)' };
    // RSI deep overbought, slope turning down = early reversal forming
    if (rsi > 70 && rsiSlope < -3) return { text: `Overbought (RSI ${rsi.toFixed(0)}) + RSI rolling over — potential bearish reversal forming.`, color: 'var(--down)' };
    // RSI deep overbought, slope still rising = continuation up
    if (rsi > 70 && rsiSlope > 1) return { text: `Overbought (RSI ${rsi.toFixed(0)}) and still climbing — bullish momentum continuing.`, color: 'var(--up)' };
    // Approaching overbought, drifting up
    if (rsi > 58 && rsiSlope > 4) return { text: `RSI ${rsi.toFixed(0)} drifting up — approaching overbought, watch for fading signal.`, color: 'var(--warn)' };
    // Approaching oversold, drifting down
    if (rsi < 42 && rsiSlope < -4) return { text: `RSI ${rsi.toFixed(0)} drifting down — approaching oversold, watch for reversal setup.`, color: 'var(--warn)' };
  }

  // VWAP + direction confluence
  if (vwapDev != null) {
    if (vwapDev < -0.5 && dir === 'bullish') return { text: `Below VWAP (${vwapDev.toFixed(2)}%) with bullish bias — potential long setup.`, color: 'var(--up)' };
    if (vwapDev > 0.5  && dir === 'bearish') return { text: `Above VWAP (+${vwapDev.toFixed(2)}%) with bearish bias — potential short setup.`, color: 'var(--down)' };
    if (vwapDev < -0.3) return { text: `Extended below VWAP (${vwapDev.toFixed(2)}%) — mean-reversion long bias.`, color: 'var(--up)' };
    if (vwapDev > 0.3)  return { text: `Extended above VWAP (+${vwapDev.toFixed(2)}%) — mean-reversion short bias.`, color: 'var(--down)' };
  }

  // Ranging / oscillating
  if (adx != null && adx < 20) {
    if (rsiSlope != null && Math.abs(rsiSlope) < 2) {
      return { text: `Ranging and oscillating — ideal for mean-reversion fades at RSI extremes.`, color: 'var(--up)' };
    }
    return { text: `Ranging (ADX ${adx.toFixed(0)}) — no strong directional edge.`, color: 'var(--faint)' };
  }

  // Default neutral
  return { text: `Mixed signals — wait for a clearer setup.`, color: 'var(--faint)' };
}

// ── Single ticker card ────────────────────────────────────────────────────────
function TickerCard({ sym, state, latestAlert, tiers }) {
  if (!state) {
    return (
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{sym}</span>
          <span style={{ fontSize: 11, color: 'var(--faint)' }}>Waiting for data…</span>
        </div>
      </div>
    );
  }

  const { rsi, vwapDev, confluence, adx, price, coil } = state;
  const regime    = getRegime(adx);
  const dir       = confluence?.direction ?? 'neutral';
  const score     = confluence?.score ?? 0;
  const arrow     = dir === 'bullish' ? '↑' : dir === 'bearish' ? '↓' : '→';
  const dirColor  = dir === 'bullish' ? 'var(--up)' : dir === 'bearish' ? 'var(--down)' : 'var(--faint)';
  const dirLabel  = dir === 'bullish' ? 'Bullish' : dir === 'bearish' ? 'Bearish' : 'Neutral';
  const summary   = deriveTrendSummary(state);

  // Alert display
  let alertEl = null;
  if (latestAlert) {
    const tierMeta  = TIER_META[latestAlert.tierId];
    const alertColor = latestAlert.severity === 'bullish' ? 'var(--up)'
                     : latestAlert.severity === 'bearish' ? 'var(--down)'
                     : 'var(--warn)';
    const ageMs  = Date.now() - latestAlert.timestamp;
    const ageSec = Math.round(ageMs / 1000);
    const ageStr = ageSec < 60   ? `${ageSec}s ago`
                 : ageSec < 3600 ? `${Math.floor(ageSec/60)}m ago`
                 : `${Math.floor(ageSec/3600)}h ago`;
    alertEl = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10,
        padding: '6px 10px', borderRadius: 5,
        background: alertColor + '12', border: `1px solid ${alertColor}40` }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: alertColor }}>
          {latestAlert.severity === 'bullish' ? '↑' : latestAlert.severity === 'bearish' ? '↓' : '⚡'}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, color: alertColor }}>{tierMeta?.name ?? latestAlert.tierId}</span>
        <span style={{ fontSize: 10, color: 'var(--text)' }}>·</span>
        <span style={{ fontSize: 10, color: 'var(--text)', flex: 1 }}>{latestAlert.label}</span>
        <span style={{ fontSize: 9, color: 'var(--faint)', fontFamily: 'monospace', flexShrink: 0 }}>{ageStr}</span>
      </div>
    );
  }

  return (
    <div style={{
      ...card,
      borderColor: coil?.coiling ? 'var(--down)' : adx != null && adx < 20 ? regime.color + '60' : 'var(--border)',
    }}>
      {/* Row 1: Symbol + price + Regime badge */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 14, color: 'var(--text)' }}>{sym}</span>
          {price != null && (
            <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--faint)' }}>${price.toFixed(2)}</span>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 3,
            letterSpacing: '.08em', background: regime.color + '20',
            color: regime.color, border: `1px solid ${regime.color}50`,
          }}>
            {regime.label.toUpperCase()}
          </span>
          {regime.scalp && (
            <span style={{ fontSize: 9, color: regime.color, textAlign: 'right', maxWidth: 200, lineHeight: 1.35, opacity: 0.85 }}>
              {regime.scalp}
            </span>
          )}
        </div>
      </div>

      {/* Row 2: Direction + RSI + VWAP dev + score */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 16, color: dirColor, lineHeight: 1 }}>{arrow}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: dirColor }}>{dirLabel}</span>
        </div>
        {rsi != null && (
          <span style={{ fontSize: 11, fontFamily: 'monospace',
            color: rsi < 30 ? 'var(--up)' : rsi > 70 ? 'var(--down)' : 'var(--muted)' }}>
            RSI {rsi.toFixed(1)}
          </span>
        )}
        {vwapDev != null && (
          <span style={{ fontSize: 11, fontFamily: 'monospace',
            color: vwapDev < -0.3 ? 'var(--up)' : vwapDev > 0.3 ? 'var(--down)' : 'var(--faint)' }}>
            {vwapDev >= 0 ? '+' : ''}{vwapDev.toFixed(2)}% VWAP
          </span>
        )}
        {dir !== 'neutral' && (
          <span style={{ fontSize: 10, color: 'var(--faint)', fontFamily: 'monospace', marginLeft: 'auto' }}>
            score {score >= 0 ? '+' : ''}{Math.round(score)}
          </span>
        )}
      </div>

      {/* Row 3: Trend summary */}
      <div style={{ fontSize: 11, color: summary.color, lineHeight: 1.45, fontStyle: summary.color === 'var(--faint)' ? 'italic' : 'normal' }}>
        {summary.text}
      </div>

      {/* Coil warning strip */}
      {coil?.coiling && (
        <div style={{ marginTop: 8, padding: '5px 8px', borderRadius: 4,
          background: 'var(--down)', color: 'var(--bg)',
          fontSize: 10, fontWeight: 700 }}>
          ⊟ {coil.daysInBand}-day coil · ${coil.bandLow}–${coil.bandHigh} · wait for break
        </div>
      )}

      {/* Latest alert */}
      {alertEl}
      {!latestAlert && (
        <div style={{ marginTop: 10, fontSize: 10, color: 'var(--faint)', fontStyle: 'italic' }}>
          No alerts yet this session
        </div>
      )}
    </div>
  );
}

// ── Tab root ──────────────────────────────────────────────────────────────────
export default function QuickMonitorTab({ engineOn, symbolStates, watchedSymbols, log, tiers, liveState, vixQuote, marketOpen }) {
  const symbols = watchedSymbols ?? ['SPY'];
  const hasData = symbols.some(s => symbolStates?.[s] != null);

  if (!engineOn) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--faint)', fontSize: 12, padding: '40px 20px' }}>
        Press <strong style={{ color: 'var(--muted)' }}>Start</strong> to begin monitoring.
      </div>
    );
  }

  if (!hasData) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--faint)', fontSize: 12, padding: '40px 20px' }}>
        Polling… first bar within a minute.
        <div style={{ marginTop: 6, fontSize: 11 }}>ADX needs ~28 bars to initialise.</div>
      </div>
    );
  }

  return (
    <div>
      <AdvicePanel liveState={liveState} vixQuote={vixQuote} marketOpen={marketOpen} />
      {symbols.map(sym => {
        // Most recent alert for this symbol
        const symAlerts  = (log ?? []).filter(e => e.symbol === sym);
        const latest     = symAlerts.length > 0
          ? symAlerts.reduce((a, b) => a.timestamp > b.timestamp ? a : b)
          : null;

        return (
          <TickerCard
            key={sym}
            sym={sym}
            state={symbolStates?.[sym] ?? null}
            latestAlert={latest}
            tiers={tiers}
          />
        );
      })}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const card = {
  padding: '12px 14px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg)',
  marginBottom: 10,
};
